/**
 * CablePathsTable Component
 *
 * Displays detailed cable path information.
 */

import React, { useMemo } from 'react';
import { GitBranch } from 'lucide-react';
import { ReportTable, ReportSection, type ReportTableColumn } from '../primitives/ReportTable';
import type { CablePathDetailRow } from '@/types/reports';

interface CablePathsTableProps {
  data: CablePathDetailRow[];
  groupByFloorPlan?: boolean;
}

export function CablePathsTable({
  data,
  groupByFloorPlan = true,
}: CablePathsTableProps) {
  const columns: ReportTableColumn<CablePathDetailRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Path',
        render: row => (
          <span className="font-medium">{row.name}</span>
        ),
        width: '120px',
      },
      {
        key: 'startJunctionName',
        header: 'Start',
        render: row => (
          <span className="text-muted-foreground">
            {row.startJunctionName ?? 'Open'}
          </span>
        ),
        width: '80px',
        hideOnMobile: true,
      },
      {
        key: 'endJunctionName',
        header: 'End',
        render: row => (
          <span className="text-muted-foreground">
            {row.endJunctionName ?? 'Open'}
          </span>
        ),
        width: '80px',
        hideOnMobile: true,
      },
      {
        key: 'route',
        header: 'Route',
        render: row => (
          <span className="text-sm">{row.route}</span>
        ),
        width: '100px',
      },
      {
        key: 'pathType',
        header: 'Type',
        render: row => (
          <span className="text-sm">{row.pathType}</span>
        ),
        width: '100px',
        hideOnMobile: true,
      },
      {
        key: 'conduitSpec',
        header: 'Conduit',
        render: row => (
          <span className="text-muted-foreground">
            {row.conduitSpec ?? '-'}
          </span>
        ),
        width: '100px',
        hideOnMobile: true,
      },
      {
        key: 'length',
        header: 'Length',
        align: 'right',
        render: row => (
          <span>
            {row.length.toLocaleString(undefined, { maximumFractionDigits: 1 })} {row.unit}
          </span>
        ),
        width: '100px',
      },
      {
        key: 'cablesDisplay',
        header: 'Cables',
        render: row => (
          <span className="text-sm whitespace-nowrap">
            {row.cablesDisplay}
          </span>
        ),
        width: '200px',
      },
    ],
    []
  );

  // Group data by floor plan if requested
  const groupedData = useMemo(() => {
    if (!groupByFloorPlan) {
      return [{ floorPlanName: null, items: data }];
    }

    const groups = new Map<string, CablePathDetailRow[]>();
    for (const row of data) {
      const key = row.floorPlanName;
      const existing = groups.get(key) ?? [];
      existing.push(row);
      groups.set(key, existing);
    }

    return Array.from(groups.entries()).map(([name, items]) => ({
      floorPlanName: name,
      items,
    }));
  }, [data, groupByFloorPlan]);

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <GitBranch className="h-12 w-12 mb-4" />
        <p>No cable paths found</p>
        <p className="text-sm">Create cable paths in your floor plans to see them here</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedData.map((group, index) => (
        <ReportSection
          key={group.floorPlanName ?? index}
          title={groupByFloorPlan && group.floorPlanName ? group.floorPlanName : undefined}
        >
          <ReportTable
            data={group.items}
            columns={columns}
            keyExtractor={row => row.id}
            emptyMessage="No paths in this floor plan"
            emptyIcon={<GitBranch className="h-8 w-8" />}
          />
        </ReportSection>
      ))}
    </div>
  );
}
